import {TeamMemberRole} from '../../../components/MembersTable/TeamMembersTable/utils';
import {OrganizationMemberRole} from '../../../components/MembersTable/util';
import {useQuery} from '../../../state/graphql/apolloWithAbort';
import {useViewer} from '../../../state/viewer/hooks';
import {useAdminModeActive, viewingAs} from '../../../util/admin';
import {VIEWER_ORG_TEAM_ACCESS} from './useViewerOrgTeamAccess.query';
import {getOrganizationMemberRole} from './util';

type ViewerOrgTeamAccessQueryData = {
  organization: {
    id: string;
    member: {id: string | null; role: string | null} | null;
    teams: Array<{
      id: string;
      name: string;
      member: {
        id: string | null;
        role: string | null;
      } | null;
    }>;
  } | null;
};

type ViewerOrgTeamAccessQueryVars = {
  orgName: string;
  viewerId: string;
  username: string;
};

export type ViewerOrgTeamAccess = {
  teamNames: string[];
  canViewAllTeams: boolean;
  isTeamAdmin: boolean;
  isLoading: boolean;
};

export const useViewerOrgTeamAccess = (
  orgName: string | undefined
): ViewerOrgTeamAccess => {
  const viewer = useViewer();
  const isViewerInternalAdmin = useAdminModeActive();

  const {data, loading} = useQuery<
    ViewerOrgTeamAccessQueryData,
    ViewerOrgTeamAccessQueryVars
  >(VIEWER_ORG_TEAM_ACCESS, {
    variables: {
      orgName: orgName ?? '',
      viewerId: viewer?.id ?? '',
      username: viewer?.username ?? '',
    },
    skip: !orgName || !viewer?.id,
  });

  const org = data?.organization;

  const isImpersonating = viewingAs() !== '';
  const viewerOrgRole = getOrganizationMemberRole(org?.member?.role ?? '');
  const canViewAllTeams =
    !isImpersonating &&
    (isViewerInternalAdmin ||
      viewerOrgRole === OrganizationMemberRole.ADMIN ||
      viewerOrgRole === OrganizationMemberRole.BILLING_ONLY);

  const teams = org?.teams ?? [];
  const allTeamNames = teams.map(t => t.name);
  const teamAdminTeamNames = teams
    .filter(t => t.member?.role === TeamMemberRole.ADMIN)
    .map(t => t.name);

  return {
    teamNames: canViewAllTeams ? allTeamNames : teamAdminTeamNames,
    canViewAllTeams,
    isTeamAdmin: teamAdminTeamNames.length > 0,
    isLoading: loading,
  };
};
